import { atom } from 'jotai'
import { atomWithStorage } from 'jotai/utils'

import type { Rundown } from '~shared/types'

import { initRundownPageStore } from '@/features/rundown/rundown-page.store'

import { assert } from '../utils/assert'
import { parseRundownDate } from '../utils/parse-rundown-date'

// -----------------------------------------------
// Rundown
// -----------------------------------------------
export const rundownAtom = atom<Rundown | null, [Rundown | null], void>(
  null,
  (_, set, updatedRundown: Rundown | null) => {
    if (!updatedRundown) {
      set(rundownAtom, null)
      return
    }

    const parsedRundown = {
      ...updatedRundown,
      startTime:
        parseRundownDate(updatedRundown.startTime) ?? updatedRundown.startTime,
      endTime: parseRundownDate(updatedRundown.endTime) ?? null,
    }

    initRundownPageStore(parsedRundown)
    set(rundownAtom, parsedRundown)
  },
)

export const rundownSettingsAtom = atom(
  (get) => get(rundownAtom)?.settings || {},
)

// -----------------------------------------------
// Rundown Columns
// -----------------------------------------------
// Column documents and everything derived from them now live in valtio:
//   `features/rundown/data/columns.store.ts`     raw documents, Firestore-fed
//   `features/rundown/column/column.derived.ts`  ordered view
// Only the ORDER stays on the rundown document (`rundown.columns`).

// -----------------------------------------------
// Rundown Cues
// -----------------------------------------------
// Cue documents and everything derived from them now live in valtio:
//   `features/rundown/data/cues.store.ts`  raw documents, Firestore-fed
//   `features/rundown/cue/cue.derived.ts`  ordered / indexed / filtered views
//   `features/rundown/cue/cue-ui.store.ts` collapsed groups, batch selection
// Only the ORDER stays here, because it is a field on the rundown document.

// rundown.cues:
// [
//   { id: string },
//   { id: string },
//   { id: string },
//   {
//     id: string,
//     children: [
//       { id: string },
//       { id: string },
//       { id: string },
//     ]
//   }
// ]

export const setCueOrderAtom = atom(
  null, // write-only atom
  (
    get,
    set,
    cueIds: Array<{ id: string; children?: Array<{ id: string }> }>,
  ) => {
    const rundown = get(rundownAtom)

    assert(rundown, 'setCueOrder: no rundown is loaded') // Reordering is only reachable from a rendered rundown.

    set(rundownAtom, { ...rundown, cues: cueIds })
  },
)

// AutoScroll functionality
interface AutoScrollState {
  [rundownId: string]: boolean
}

const internalAutoScrollAtom = atomWithStorage<AutoScrollState>(
  'autoScroll',
  {},
)

export const autoScrollAtom = atom(
  (get) => {
    return get(internalAutoScrollAtom)
  },
  (get, set, rundownId: string, newValue: boolean) => {
    const current = get(internalAutoScrollAtom)

    const updated = { ...current, [rundownId]: newValue }

    set(internalAutoScrollAtom, updated)
  },
)
